import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Euler, type Object3D, Vector3 } from 'three';

import { isMhrScene } from '@cartwhl/3d/gltf/utils';
import {
  type DeepPartial,
  Player,
  type PlayerCameraControlsHandle,
  type PlayerSettings,
  usePlayerStore,
  usePlayerTimeline,
} from '@cartwhl/3d/player';
import Loading from '@cartwhl/ui/atoms/Loading';
import Slider from '@cartwhl/ui/atoms/Slider';
import Text from '@cartwhl/ui/base/TextNew';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import GoButton from '@cartwhl/ui/input/GoButton';

import { CHARACTER_ROTATION } from '../../../../MotionEditor/constants';
import { getBuiltinFreeCharacterMeshPath } from '../../../../shared/freeCharacterMesh';

const PREVIEW_ACTOR_ID = 'cartographer-actor-picker';
const DEFAULT_CHARACTER_ID = 'char-upload-Axel';
const MHR_CHARACTER_ID = 'char-upload-MHR';
const DEFAULT_MESH_PATH =
  getBuiltinFreeCharacterMeshPath(DEFAULT_CHARACTER_ID) ?? '';

export interface CartographerMotionActorPickerStageProps {
  jobId: string;
  bvhURLs: string[];
  mhrBvhURLs?: string[];
  mhrIdentityJsonURLs?: string[];
}

function resolvePreviewCharacterId(
  mhrBvhURLs: string[] | undefined,
  mhrIdentityJsonURLs: string[] | undefined,
): string {
  const mhrAligned =
    !!mhrBvhURLs?.length &&
    !!mhrIdentityJsonURLs?.length &&
    mhrBvhURLs.length === mhrIdentityJsonURLs.length &&
    mhrBvhURLs.some((url) => url.trim()) &&
    mhrIdentityJsonURLs.some((url) => url.trim());
  return mhrAligned ? MHR_CHARACTER_ID : DEFAULT_CHARACTER_ID;
}

function ActorPickerTransportBar({ actorIds }: { actorIds: string[] }) {
  const store = usePlayerStore();
  const primaryActorId = actorIds[0] ?? PREVIEW_ACTOR_ID;
  const { isPlaying, currentFrame, duration } =
    usePlayerTimeline(primaryActorId);
  const totalFrames = duration.frames;

  const playOrPause = useCallback(() => {
    if (actorIds.length === 0) return;
    if (store.state.transport.isPlaying) {
      for (const id of actorIds) {
        store.pause(id);
      }
    } else {
      for (const id of actorIds) {
        store.play(id);
      }
    }
  }, [actorIds, store]);

  const setFrameAll = useCallback(
    (frame: number) => {
      for (const id of actorIds) {
        store.setFrame(frame, id);
      }
    },
    [actorIds, store],
  );

  return (
    <div className="pointer-events-auto flex min-w-0 items-center gap-2">
      <GoButton
        icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
        onClick={() => playOrPause()}
      />
      <div className="text-gray-90 relative flex h-full items-center">
        <Text
          as="div"
          token="14/medium"
          className="text-gray-90 text-end opacity-60"
          style={{
            width:
              totalFrames > 0 ? totalFrames.toString().length * 21 : undefined,
          }}
        >
          {!!totalFrames && (
            <>
              {currentFrame + 1}/{totalFrames}
            </>
          )}
        </Text>
      </div>
      <div className="pointer-events-auto flex h-10 min-w-0 flex-1 items-center px-2">
        <Slider
          currentValue={totalFrames > 0 ? currentFrame + 1 : 1}
          min={1}
          max={Math.max(totalFrames, 1)}
          step={1}
          onChange={(value) => setFrameAll(value - 1)}
        />
      </div>
    </div>
  );
}

export default function CartographerMotionActorPickerStage({
  jobId,
  bvhURLs,
  mhrBvhURLs,
  mhrIdentityJsonURLs,
}: CartographerMotionActorPickerStageProps) {
  const [fitTarget, setFitTarget] = useState<Object3D | null>(null);
  const [rootRotation, setRootRotation] = useState<Euler>(CHARACTER_ROTATION);
  const cameraControlsRef = useRef<PlayerCameraControlsHandle | null>(null);

  const characterID = useMemo(
    () => resolvePreviewCharacterId(mhrBvhURLs, mhrIdentityJsonURLs),
    [mhrBvhURLs, mhrIdentityJsonURLs],
  );
  const meshPath = useMemo(
    () => getBuiltinFreeCharacterMeshPath(characterID) ?? DEFAULT_MESH_PATH,
    [characterID],
  );

  const { animationUrls, animationIdentityPaths } = useMemo(() => {
    if (
      characterID === MHR_CHARACTER_ID &&
      mhrBvhURLs?.length &&
      mhrIdentityJsonURLs?.length === mhrBvhURLs.length
    ) {
      const urls: string[] = [];
      const identityPaths: (string | undefined)[] = [];
      for (let i = 0; i < mhrBvhURLs.length; i += 1) {
        const bvh = mhrBvhURLs[i]?.trim();
        if (!bvh) continue;
        urls.push(bvh);
        const identity = mhrIdentityJsonURLs[i]?.trim();
        identityPaths.push(identity || undefined);
      }
      if (urls.length > 0) {
        return {
          animationUrls: urls,
          animationIdentityPaths: identityPaths,
        };
      }
    }
    const urls = bvhURLs.map((url) => url.trim()).filter(Boolean);
    return { animationUrls: urls, animationIdentityPaths: undefined };
  }, [bvhURLs, characterID, mhrBvhURLs, mhrIdentityJsonURLs]);

  const actorIds = useMemo(
    () =>
      animationUrls.map((_, index) =>
        animationUrls.length <= 1
          ? PREVIEW_ACTOR_ID
          : `${PREVIEW_ACTOR_ID}-${index}`,
      ),
    [animationUrls],
  );

  const characterPositions = useMemo(
    () => animationUrls.map(() => new Vector3(0, 0, 0)),
    [animationUrls],
  );

  const fitCharacterKey = useMemo(
    () => animationUrls.join('|'),
    [animationUrls],
  );

  const playerRootId = useMemo(
    () =>
      `cartographer-actor-picker-${jobId.replace(/[^a-zA-Z0-9_-]/g, '-') || 'job'}`,
    [jobId],
  );

  const initialSettings = useMemo<DeepPartial<PlayerSettings>>(
    () => ({
      playback: {
        frameRate: 30,
        frameStepSize: 1,
        autoplay: true,
        loop: true,
      },
      character: {
        showSkeleton: false,
      },
      environment: {
        showGrid: true,
        gridCellSize: 1,
      },
      scene: {
        showActorNameTags: animationUrls.length > 1,
        dimInactiveActorNameTags: false,
      },
    }),
    [animationUrls.length],
  );

  useEffect(() => {
    setFitTarget(null);
    setRootRotation(CHARACTER_ROTATION);
  }, [jobId, fitCharacterKey]);

  const handlePrimarySceneReady = useCallback((scene: Object3D) => {
    setFitTarget(scene);
    setRootRotation(
      isMhrScene(scene) ? new Euler(0, 0, 0) : CHARACTER_ROTATION,
    );
  }, []);

  useEffect(() => {
    if (!fitTarget) return undefined;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 36;

    const tryFrame = () => {
      if (cancelled) return;
      const ok = cameraControlsRef.current?.frame?.(true) ?? false;
      attempts += 1;
      if (!ok && attempts < maxAttempts) {
        window.setTimeout(tryFrame, 50);
      }
    };

    tryFrame();
    return () => {
      cancelled = true;
    };
  }, [fitTarget, fitCharacterKey]);

  if (animationUrls.length === 0) {
    return (
      <div className="border-primary bg-secondary dark:bg-gray-90 relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl border">
        <Loading className="text-secondary" />
      </div>
    );
  }

  return (
    <div className="border-primary bg-secondary dark:bg-gray-90 relative aspect-video w-full overflow-hidden rounded-xl border">
      <Player.Root
        key={playerRootId}
        id={playerRootId}
        hotkeysEnabled={false}
        initialSettings={initialSettings}
      >
        <Player.Canvas className="absolute inset-0 h-full w-full rounded-none! bg-transparent!">
          <Player.Environment />
          <Player.DefaultCamera position={[0, 1, 10]} zoom={2} />
          <Player.CameraControls
            cameraControlsRef={cameraControlsRef}
            fitCharacter
            fitCharacterKey={fitCharacterKey}
            fitCharacterTarget={fitTarget}
            fitCharacterDistanceMultiplier={1.15}
          />
          <group rotation={rootRotation}>
            {animationUrls.map((url, index) => (
              <Player.Character
                key={`${actorIds[index]}-${index}`}
                actorId={actorIds[index]!}
                characterID={characterID}
                meshPath={meshPath}
                meshFormat="glb"
                animation={{ type: 'url', url }}
                identityPath={animationIdentityPaths?.[index]}
                position={characterPositions[index]!}
                onSceneReady={
                  index === 0 ? handlePrimarySceneReady : undefined
                }
              />
            ))}
          </group>
        </Player.Canvas>
        <div className="pointer-events-none absolute right-3 bottom-3 left-3 z-10">
          <ActorPickerTransportBar actorIds={actorIds} />
        </div>
        <div className="pointer-events-auto absolute top-3 left-3 z-20">
          <Player.CameraButtons
            cameraControlsRef={cameraControlsRef}
            inModal
            variant="simple"
            tooltipSide="right"
          />
        </div>
      </Player.Root>
    </div>
  );
}
